import { animated, config, easings, useSpring } from '@react-spring/web';
import type { Key, ReactElement } from 'react';
import { Children, useEffect, useRef } from 'react';
import { useMeasure } from 'react-use';
import styled from 'styled-components';

import { hapticFeedback } from '@sorare/core/src/lib/haptic';

const ITEM_WIDTH = 200;
const ITEM_SCALE = 0.8;
const ITEM_SCALED_WIDTH = ITEM_WIDTH * ITEM_SCALE;
const ITEM_HEIGHT = 317;
const GAP = 16;

const Root = styled.div`
  position: relative;
  width: 100%;
  height: ${ITEM_HEIGHT}px;
  box-sizing: content-box;

  background-color: var(--c-nl-300);
  padding: var(--double-unit) 0;
  backdrop-filter: blur(40px);
`;

const Item = styled(animated.div)`
  position: absolute;

  width: ${ITEM_WIDTH}px;
  height: ${ITEM_HEIGHT}px;
`;

const Bar = styled(animated.div)`
  position: absolute;
  box-shadow: 0 0 var(--unit) var(--c-nl-500);
  --width: var(--half-unit);
  width: var(--width);
  top: calc(-1 * var(--unit));
  left: calc(50% - var(--width) / 2);
  bottom: calc(-1 * var(--unit));
  background-color: var(--c-white);
`;

const getPosition = (
  index: number,
  currentIndex: number,
  nbElements: number
): number => {
  const currentIndexModulo = currentIndex % nbElements;
  if (index - currentIndexModulo < nbElements / -2) {
    return index - currentIndexModulo + nbElements;
  }
  if (index - currentIndexModulo > nbElements / 2) {
    return index - currentIndexModulo - nbElements;
  }
  return index - currentIndexModulo;
};

type Props = {
  children: ReactElement<{ config: { id: Key } }>[];
  spin: boolean;
  stopAtIndex?: number;
  immediate?: boolean;
  onSpinEnd?: () => void;
};
export const Spinner = ({
  spin,
  children,
  stopAtIndex,
  immediate,
  onSpinEnd,
}: Props) => {
  const [measurementDiv, { width }] = useMeasure<HTMLDivElement>();
  const nbElements = Children.count(children);

  const isSpinning = useRef(false);
  const isStopping = useRef(false);

  const audioContextRef = useRef<AudioContext>(null!);

  useEffect(() => {
    if (!window.AudioContext) {
      return () => {};
    }
    audioContextRef.current = new AudioContext();
    return () => {
      audioContextRef.current?.close();
    };
  }, []);

  function playClick(freq: number) {
    if (!audioContextRef.current) {
      return;
    }

    const osc = audioContextRef.current.createOscillator();
    const gain = audioContextRef.current.createGain();
    osc.connect(gain);
    gain.connect(audioContextRef.current.destination);

    osc.type = 'triangle';
    osc.frequency.value = freq;

    gain.gain.setValueAtTime(0.02, audioContextRef.current.currentTime);
    gain.gain.linearRampToValueAtTime(
      0.002,
      audioContextRef.current.currentTime + 0.05
    ); // attack
    gain.gain.exponentialRampToValueAtTime(
      0.001,
      audioContextRef.current.currentTime + 0.025
    ); // decay

    osc.start();
    osc.stop(audioContextRef.current.currentTime + 0.05);
  }

  const immediateRef = useRef(immediate);
  immediateRef.current = immediate;
  const onSpinEndRef = useRef(onSpinEnd);
  onSpinEndRef.current = onSpinEnd;

  const pointingAtIndex = useRef(0);
  const [style, api] = useSpring(
    () => ({
      from: { currentIndex: 0 },

      onChange: ({ value }) => {
        const currentlyPointingAtIndex =
          Math.floor(value.currentIndex - 0.499) % nbElements;
        if (currentlyPointingAtIndex !== pointingAtIndex.current) {
          if (!(immediateRef.current && stopAtIndex !== undefined)) {
            playClick(900);
            hapticFeedback();
          }
          pointingAtIndex.current = currentlyPointingAtIndex;
        }
      },
      onRest: ({ value }) => {
        if (
          stopAtIndex !== undefined &&
          isSpinning.current &&
          !isStopping.current &&
          !immediateRef.current
        ) {
          const randomOffset = Math.random() * 0.99 - 0.49;

          api.start({
            from: { currentIndex: value.currentIndex },
            to: [
              {
                currentIndex:
                  value.currentIndex + stopAtIndex + nbElements + randomOffset,
                onRest: () => {
                  // prevent end animation from being called again
                  isStopping.current = true;
                },
              },
              // center the item
              {
                currentIndex: value.currentIndex + stopAtIndex + nbElements,
                config: config.gentle,
                onRest: () => {
                  // Spin is over
                  onSpinEndRef.current?.();
                },
              },
            ],
            config: {
              easing: easings.easeOutCubic,
              duration: (3000 / nbElements) * (stopAtIndex + nbElements),
            },
          });
        }
        if (stopAtIndex === undefined && isSpinning.current) {
          // keep spinning
          api.start({
            to: {
              currentIndex: value.currentIndex + nbElements,
            },
            config: {
              easing: easings.linear,
              duration: 1000,
            },
          });
        }
      },
    }),
    [stopAtIndex, nbElements]
  );

  useEffect(() => {
    if (!immediate || stopAtIndex === undefined) return;

    isStopping.current = true;

    api.start({
      to: { currentIndex: stopAtIndex },
      immediate: true,
      onRest: () => onSpinEndRef.current?.(),
    });
  }, [immediate, stopAtIndex, api, nbElements, style.currentIndex]);

  if (spin && !isSpinning.current) {
    isSpinning.current = true;

    api.start(() => {
      return {
        from: { currentIndex: 0 },
        to: { currentIndex: nbElements },
        onStart: () => {
          isSpinning.current = true;
        },
        config: {
          easing: easings.linear,
          duration: 1000,
        },
      };
    });
  }

  if (!spin) {
    isSpinning.current = false;
    isStopping.current = false;
  }

  return (
    <Root ref={measurementDiv}>
      {Children.map(children, (child, i) => (
        <Item
          // using index as keys here would wrongly reuse former element when shuffling slots
          key={child.props.config.id}
          style={{
            transform: style.currentIndex.to(animatedCurrentIndex => {
              const position = getPosition(i, animatedCurrentIndex, nbElements);
              return `translateX(${
                // offset to center
                (width - ITEM_WIDTH) / 2 +
                // figure out index
                position * (ITEM_SCALED_WIDTH + GAP) +
                // Adjust position to take zoom into account
                (Math.min(1, Math.max(-1, position)) *
                  (ITEM_WIDTH - ITEM_SCALED_WIDTH)) /
                  2
              }px) scale(${
                (1 - Math.min(1, Math.abs(position))) * 0.2 + ITEM_SCALE
              })`;
            }),
            opacity: style.currentIndex.to(animatedCurrentIndex => {
              const position = getPosition(i, animatedCurrentIndex, nbElements);
              return position === 0 ? 1 : 1 / Math.abs(position);
            }),
          }}
        >
          {child}
        </Item>
      ))}
      <Bar />
    </Root>
  );
};
